import { Modal } from 'antd'
import CantileverButton from 'components/CantileverButton'
import { ConfirmDeletePopup } from 'components/ConfirmDeletePopup'
import {
  DownloadIcon,
  StyledTrashIcon,
} from 'features/assetDatabase/utils/sharedStyles'
import { AssetOverviewEditorGate } from 'features/auth'
import { useRolodexEdit } from 'features/fde'
import {
  useRolodexEntries,
  useRolodexFileLS,
  useRolodexSignedURLs,
} from 'features/rolodex'
import { PaperclipIcon } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { GeckoFileCategory, GeckoUnit, RolodexEntryType } from 'types'

import { EditTagsModal } from './EditTagsModal'
import { BasicComponent } from './FilesTable'

const EntryActionStyledCell = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: end;
  min-width: 5rem;
  gap: 0.25rem;
`

export const EntryActionCell: React.FC<{
  downloadFirstFile: boolean
  deleteEntry: boolean
  entryId: string
  fileName: string
  fileCategory: GeckoFileCategory
  forceRefresh: () => void
  components: BasicComponent[]
  unit?: GeckoUnit
  refreshData: () => Promise<void>
  refreshThumbnails: () => Promise<void>
  setIsPrimaryImageLoaded: (loaded: boolean) => void
}> = ({
  downloadFirstFile,
  deleteEntry,
  entryId,
  fileName,
  fileCategory,
  forceRefresh,
  components,
  unit,
  refreshData,
  refreshThumbnails,
  setIsPrimaryImageLoaded,
}) => {
  const { fileRefs } = useRolodexFileLS({
    entry_id: entryId,
    path: '**',
  })

  const { reports, isLoading } = useRolodexEntries({
    type: RolodexEntryType.REPORT,
    ids: [entryId],
  })

  const { rolodexPatch, rolodexDelete } = useRolodexEdit()
  const { url } = useRolodexSignedURLs(fileRefs)

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isTagModalOpen, setIsTagModalOpen] = useState(false)
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 })
  const [modalComponentsTags, setModalComponentsTags] = useState<
    { name: string; id: string }[]
  >([])

  useEffect(() => {
    setModalComponentsTags(
      components.filter((component) =>
        reports[0]?.data.components?.includes(component.id)
      )
    )
  }, [components, reports])

  const handleDelete = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (reports[0] !== undefined) {
      // if the report is the primary image of the unit, remove the primary image
      if (unit?.data.primary_image === reports[0].id) {
        await rolodexPatch([
          {
            id: unit.id,
            delete_data: ['primary_image'],
            tags: unit.tags,
          },
        ])
        await refreshData()
        setIsPrimaryImageLoaded(false)
      }
      await rolodexDelete(reports[0].id, true)
      forceRefresh()
      if (fileCategory === GeckoFileCategory.IMAGE) {
        await refreshThumbnails()
      }
      setIsDialogOpen(false)
    }
  }

  const downloadFile = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (!url) {
      console.error('No URL available for download')
      return
    }
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.style.display = 'none'
      anchor.href = blobUrl
      anchor.download = fileName
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
      anchor.remove()
    } catch (error) {
      console.error('Error downloading the file:', error)
    }
  }

  const openDeleteDialog = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    const rect = event.currentTarget.getBoundingClientRect()
    setPopupPosition({
      top: rect.bottom + 8, // 8px below the delete icon
      left: rect.left + rect.width / 2, // Centered on delete icon
    })
    setIsDialogOpen(true)
  }

  const handleTagEdit = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    setIsTagModalOpen(true)
  }

  const handleCloseTagModal = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    setIsTagModalOpen(false)
  }

  const handleSubmitTagModal = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    rolodexPatch([
      {
        id: reports[0]?.id,
        data: {
          components: modalComponentsTags.map((component) => component.id),
        },
      },
    ])
      .then(() => {
        forceRefresh()
        setIsTagModalOpen(false)
      })
      .catch((error) => {
        console.error('Error updating tags:', error)
        setIsTagModalOpen(false)
      })
  }

  return (
    <>
      <EntryActionStyledCell>
        {downloadFirstFile && !isDialogOpen && url && (
          <CantileverButton iconOnly ghost onClick={downloadFile}>
            <DownloadIcon />
          </CantileverButton>
        )}
        <AssetOverviewEditorGate>
          <CantileverButton
            iconOnly
            ghost
            onClick={handleTagEdit}
            title='Edit Component Tags'>
            <PaperclipIcon />
          </CantileverButton>
          {deleteEntry && !isLoading && !isDialogOpen && (
            <CantileverButton iconOnly ghost onClick={openDeleteDialog}>
              <StyledTrashIcon />
            </CantileverButton>
          )}
        </AssetOverviewEditorGate>
        {deleteEntry && !isLoading && isDialogOpen && (
          <ConfirmDeletePopup
            entityName='file'
            popupPosition={popupPosition}
            cancelDelete={() => setIsDialogOpen(false)}
            confirmDelete={handleDelete}
            deleteMessage='Are you sure you want to delete this file?'
          />
        )}
        <Modal
          title='Edit ComponentTags'
          open={isTagModalOpen}
          onCancel={handleCloseTagModal}
          footer={[
            <CantileverButton key='submit' onClick={handleSubmitTagModal}>
              Submit
            </CantileverButton>,
          ]}>
          <EditTagsModal
            componentsTags={modalComponentsTags}
            setComponentsTags={setModalComponentsTags}
            components={components}
          />
        </Modal>
      </EntryActionStyledCell>
    </>
  )
}
